import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { useApi } from '../hooks/useApi';
import { useTheme } from '../context/ThemeContext';
import { useSettings } from '../context/SettingsContext';
import { ShoppingCart, Eye, Calendar, Search, X, ArrowLeft, ArrowRight, XCircle } from 'lucide-react';
import Select from 'react-select';
import LoadingSpinner from '../components/LoadingSpinner';

function HistorikuShitjeve() {
  const { theme } = useTheme();
  const { getCurrency, getCurrencyCode } = useSettings();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [lokacionFilter, setLokacionFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedShitje, setSelectedShitje] = useState(null);
  const [loadingDetaje, setLoadingDetaje] = useState(false);

  // Fetch lokacionet për filter
  const { data: lokacionetData } = useApi(
    () => apiService.get('/lokacionet'),
    [],
    { immediate: true, showNotifications: false }
  );

  const lokacionet = Array.isArray(lokacionetData) ? lokacionetData : (lokacionetData?.data || []);

  // Fetch shitjet me pagination
  const getParams = () => {
    const params = {
      page: currentPage,
      per_page: perPage,
    };
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    if (lokacionFilter) params.lokacion_id = lokacionFilter;
    if (searchTerm) params.search = searchTerm;
    return params;
  };

  const { data: shitjetData, loading, execute: refetchShitjet } = useApi(
    () => apiService.get('/shitje', { params: getParams() }),
    [currentPage, fromDate, toDate, lokacionFilter, searchTerm],
    { immediate: true, showNotifications: false }
  );

  const shitjet = shitjetData?.data || [];
  const pagination = shitjetData?.pagination || {};

  // Fetch detajet e shitjes së zgjedhur
  const handleViewDetaje = async (shitjeId) => {
    setLoadingDetaje(true);
    try {
      const response = await apiService.get(`/shitje/${shitjeId}`);
      // API kthen { success: true, data: {...} } ose direkt objektin
      // apiService.get() tashmë ekstrakton response.data.data ose response.data
      if (response) {
        setSelectedShitje(response);
      } else {
        throw new Error('Shitja nuk u gjet');
      }
    } catch (error) {
      console.error('Gabim gjatë marrjes së detajeve:', error);
      // Shfaq mesazh më informativ për përdoruesin
      const errorMessage = error.response?.data?.message || error.message || 'Shitja nuk u gjet ose ka ndodhur një gabim.';
      alert(errorMessage);
    } finally {
      setLoadingDetaje(false);
    }
  };

  const closeModal = () => {
    setSelectedShitje(null);
  };

  // Reset filters
  const handleResetFilters = () => {
    setFromDate('');
    setToDate('');
    setLokacionFilter('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('sq-AL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format currency - merr monedhën nga settings
  const formatCurrency = (amount) => {
    // Merr kodin e monedhës direkt nga settings përmes getCurrencyCode()
    const currencyCode = getCurrencyCode();
    
    // Formatizo si faqet e tjera - përdor .toFixed(2) dhe shto kodin e monedhës
    return `${Number(amount).toFixed(2)} ${currencyCode}`;
  };

  // Custom styles for react-select
  const selectStyles = theme === 'dark' ? {
    control: (base, state) => ({
      ...base,
      backgroundColor: '#1e293b',
      borderColor: state.isFocused ? '#f59e0b' : '#475569',
      boxShadow: state.isFocused ? '0 0 0 1px #f59e0b' : 'none',
      '&:hover': { borderColor: '#f59e0b' },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: '#1e293b',
      borderColor: '#475569',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#78350f' : state.isSelected ? '#f59e0b' : '#1e293b',
      color: state.isSelected ? 'white' : '#e2e8f0',
    }),
    singleValue: (base) => ({ ...base, color: '#e2e8f0' }),
    input: (base) => ({ ...base, color: '#e2e8f0' }),
    placeholder: (base) => ({ ...base, color: '#94a3b8' }),
  } : {
    control: (base, state) => ({
      ...base,
      backgroundColor: 'transparent',
      borderColor: state.isFocused ? '#f59e0b' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #f59e0b' : 'none',
      '&:hover': { borderColor: '#f59e0b' },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: 'white',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#fef3c7' : state.isSelected ? '#fbbf24' : 'white',
      color: state.isSelected ? 'white' : '#111827',
    }),
  };

  const lokacionOptions = lokacionet.map(l => ({
    value: l.id,
    label: l.emri
  }));

  return (
    <div className="w-full animate-fade-in">
      <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gradient-logo flex items-center gap-3 animate-slide-down">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
          <ShoppingCart size={32} className="text-primary-600 dark:text-primary-400" />
        </div>
        Historiku i Shitjeve
      </h2>

      {/* Filters */}
      <div className="card-premium p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-dark-400" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Kërko kupon kodi..."
              className="input-premium pl-10"
            />
          </div>

          {/* From Date */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-dark-400" size={18} />
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setCurrentPage(1);
              }}
              className="input-premium pl-10"
            />
          </div>

          {/* To Date */}
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-dark-400" size={18} />
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setCurrentPage(1);
              }}
              className="input-premium pl-10"
            />
          </div>

          {/* Lokacion Filter */}
          <Select
            placeholder="Lokacioni"
            isClearable
            value={lokacionOptions.find(o => o.value === lokacionFilter) || null}
            onChange={(option) => {
              setLokacionFilter(option ? option.value : '');
              setCurrentPage(1);
            }}
            options={lokacionOptions}
            styles={selectStyles}
            classNamePrefix="react-select"
          />
        </div>

        {/* Reset Button */}
        {(fromDate || toDate || lokacionFilter || searchTerm) && (
          <button
            onClick={handleResetFilters}
            className="mt-4 btn-warning flex items-center gap-2"
          >
            <X size={18} />
            Pastro Filtra
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card-premium overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto -mx-6 px-6">
              <table className="table-premium w-full min-w-[800px]">
                <thead>
                  <tr>
                    <th className="hidden sm:table-cell">#</th>
                    <th>Kupon Kodi</th>
                    <th className="hidden md:table-cell">Data</th>
                    <th className="hidden lg:table-cell">Shitësi</th>
                    <th className="hidden lg:table-cell">Lokacioni</th>
                    <th className="hidden sm:table-cell">Nr. Produkteve</th>
                    <th>Totali</th>
                    <th>Veprime</th>
                  </tr>
                </thead>
                <tbody>
                  {shitjet.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-12 text-gray-500 dark:text-gray-400">
                        <div className="flex flex-col items-center gap-3">
                          <ShoppingCart size={48} className="text-gray-300 dark:text-dark-600" />
                          <p className="text-lg">Nuk u gjetën shitje</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    shitjet.map((shitje, index) => (
                      <tr key={shitje.id} className="hover:bg-primary-50/50 dark:hover:bg-primary-900/10 transition-colors">
                        <td className="hidden sm:table-cell">{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                        <td className="font-medium font-mono">
                          <div className="flex flex-col">
                            <span>{shitje.kupon_kodi}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 md:hidden">
                              {formatDate(shitje.data || shitje.created_at)}
                            </span>
                          </div>
                        </td>
                        <td className="hidden md:table-cell">{formatDate(shitje.data || shitje.created_at)}</td>
                        <td className="hidden lg:table-cell">{shitje.user?.emri || '-'}</td>
                        <td className="hidden lg:table-cell">{shitje.lokacion?.emri || '-'}</td>
                        <td className="text-center hidden sm:table-cell">
                          <span className="badge-primary">{shitje.numri_produkteve || 0}</span>
                        </td>
                        <td className="font-bold text-primary-600 dark:text-primary-400">
                          {formatCurrency(shitje.totali)}
                        </td>
                        <td>
                          <button
                            onClick={() => handleViewDetaje(shitje.id)}
                            className="btn-primary flex items-center gap-2 text-sm px-2 py-1"
                            title="Shiko Detaje"
                          >
                            <Eye size={16} />
                            <span className="hidden sm:inline">Shiko</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.last_page > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-800/50">
                <div className="text-sm text-gray-600 dark:text-dark-400 text-center sm:text-left">
                  Duke shfaqur {pagination.from || 0} - {pagination.to || 0} nga {pagination.total || 0} shitje
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={pagination.current_page === 1}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm px-3 py-2"
                  >
                    <ArrowLeft size={16} />
                    <span className="hidden sm:inline">Para</span>
                  </button>
                  <span className="px-2 sm:px-4 py-2 text-sm font-medium text-gray-700 dark:text-dark-300">
                    <span className="hidden sm:inline">Faqja </span>{pagination.current_page}<span className="hidden sm:inline"> nga {pagination.last_page}</span>
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(pagination.last_page, p + 1))}
                    disabled={pagination.current_page === pagination.last_page}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm px-3 py-2"
                  >
                    <span className="hidden sm:inline">Pas</span>
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal për Detajet */}
      {selectedShitje && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60 backdrop-blur-sm px-4 animate-fadeIn">
          <div className="relative card-premium w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-slide-down p-6">
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              aria-label="Mbylle"
            >
              <XCircle size={26} />
            </button>

            {loadingDetaje ? (
              <div className="flex justify-center items-center py-20">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="mb-6 border-b border-gray-200 dark:border-dark-700 pb-4">
                  <h3 className="text-2xl font-bold text-gradient-logo mb-2">
                    Detajet e Shitjes
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 dark:text-dark-400">Kupon Kodi</p>
                      <p className="font-mono font-bold">{selectedShitje.kupon_kodi}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-dark-400">Data</p>
                      <p className="font-medium">{formatDate(selectedShitje.data || selectedShitje.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-dark-400">Shitësi</p>
                      <p className="font-medium">{selectedShitje.user?.emri || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-dark-400">Lokacioni</p>
                      <p className="font-medium">{selectedShitje.lokacion?.emri || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Detajet e Produkteve */}
                <div className="mb-6">
                  <h4 className="text-lg font-bold mb-4 text-gray-700 dark:text-dark-300">
                    Produktet e Shitura
                  </h4>
                  <div className="overflow-x-auto -mx-6 px-6">
                    <table className="table-premium w-full min-w-[600px]">
                      <thead>
                        <tr>
                          <th className="hidden sm:table-cell">#</th>
                          <th>Produkti</th>
                          <th className="hidden md:table-cell">Madhësia</th>
                          <th className="hidden md:table-cell">Ngjyra</th>
                          <th>Sasia</th>
                          <th className="hidden sm:table-cell">Çmimi</th>
                          <th>Nëntotali</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedShitje.detaje?.length > 0 ? (
                          selectedShitje.detaje.map((detaje, index) => (
                            <tr key={detaje.id}>
                              <td className="hidden sm:table-cell">{index + 1}</td>
                              <td className="font-medium">
                                <div className="flex flex-col">
                                  <span>{detaje.produkt?.emri || '-'}</span>
                                  {detaje.produkt?.kodi && (
                                    <span className="text-xs text-gray-500 dark:text-dark-400">
                                      ({detaje.produkt.kodi})
                                    </span>
                                  )}
                                  <span className="text-xs text-gray-500 dark:text-gray-400 md:hidden">
                                    {detaje.madhesi?.emri || '-'} / {detaje.ngjyra?.emri || '-'}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400 sm:hidden">
                                    {formatCurrency(detaje.cmimi_shitjes)} × {detaje.sasia}
                                  </span>
                                </div>
                              </td>
                              <td className="hidden md:table-cell">{detaje.madhesi?.emri || '-'}</td>
                              <td className="hidden md:table-cell">{detaje.ngjyra?.emri || '-'}</td>
                              <td className="text-center">{detaje.sasia}</td>
                              <td className="hidden sm:table-cell">{formatCurrency(detaje.cmimi_shitjes)}</td>
                              <td className="font-bold text-primary-600 dark:text-primary-400">
                                {formatCurrency(detaje.sasia * detaje.cmimi_shitjes)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="text-center py-8 text-gray-500 dark:text-gray-400">
                              Nuk ka detaje
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totali */}
                <div className="flex justify-end border-t border-gray-200 dark:border-dark-700 pt-4">
                  <div className="text-right">
                    <p className="text-sm text-gray-500 dark:text-dark-400 mb-2">Totali:</p>
                    <p className="text-3xl font-bold text-gradient-logo">
                      {formatCurrency(selectedShitje.totali)}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HistorikuShitjeve;


